import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload as UploadIcon, Image, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tattooStyles = [
  'Tradicional', 'Neo-Tradicional', 'Realismo', 'Blackwork', 
  'Dotwork', 'Japonés', 'Tribal', 'Minimalista', 
  'Acuarela', 'Geométrico', 'Chicano', 'Lettering'
];

const Upload = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    style: '',
    tags: ''
  });

  if (user?.user_type !== 'artist') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="font-['Cinzel'] text-2xl font-bold text-white mb-4">
          Sección solo para tatuadores
        </h2>
        <p className="text-zinc-500">
          Esta función está disponible únicamente para cuentas de tatuadores.
        </p>
      </div>
    );
  }

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no puede superar los 10MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result);
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImageUrl(response.data.url);
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir la imagen');
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageUrl) {
      toast.error('Por favor sube una imagen');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setUploading(true);
    try {
      const tags = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      await axios.post(`${API}/artworks`, {
        ...formData,
        image_url: imageUrl,
        tags
      });

      toast.success('¡Trabajo publicado exitosamente!');
      navigate('/feed');
    } catch (error) {
      console.error('Error creating artwork:', error);
      toast.error('Error al publicar el trabajo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="upload-container">
      <div className="text-center mb-10">
        <h1 className="font-['Cinzel'] text-3xl md:text-4xl font-bold text-white mb-2">
          SUBIR TRABAJO
        </h1>
        <p className="text-zinc-500">Comparte tu arte con la comunidad</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Image Upload */}
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-4 block">
            Imagen del tatuaje *
          </Label>
          
          {imagePreview ? (
            <div className="relative aspect-[4/5] max-w-md mx-auto bg-zinc-900 overflow-hidden">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              {uploading && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                data-testid="remove-image-btn"
              >
                <X size={20} />
              </button>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="aspect-[4/5] max-w-md mx-auto border-2 border-dashed border-zinc-800 hover:border-zinc-700 flex flex-col items-center justify-center cursor-pointer transition-colors"
              data-testid="upload-dropzone"
            >
              <Image className="w-12 h-12 text-zinc-700 mb-4" />
              <p className="text-zinc-500 mb-2">Haz clic para seleccionar una imagen</p>
              <p className="text-xs text-zinc-600">JPG, PNG o WEBP (máx. 10MB)</p>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
        </div>

        {/* Form Fields */}
        <div className="space-y-6">
          <div>
            <Label htmlFor="title" className="text-xs uppercase tracking-wider text-zinc-500">
              Título *
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="mt-2 bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 py-4 text-lg focus:border-zinc-100 focus-visible:ring-0"
              placeholder="Nombre de tu trabajo"
              data-testid="title-input"
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-xs uppercase tracking-wider text-zinc-500">
              Descripción
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="mt-2 bg-transparent border-zinc-800 min-h-[100px]"
              placeholder="Cuéntanos sobre este trabajo..."
              data-testid="description-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Estilo
            </Label>
            <Select
              value={formData.style}
              onValueChange={(value) => setFormData(prev => ({ ...prev, style: value }))}
            >
              <SelectTrigger className="mt-2 bg-transparent border-zinc-800" data-testid="style-select">
                <SelectValue placeholder="Selecciona un estilo" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {tattooStyles.map((style) => (
                  <SelectItem key={style} value={style}>{style}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="tags" className="text-xs uppercase tracking-wider text-zinc-500">
              Etiquetas
            </Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              className="mt-2 bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 py-4 focus:border-zinc-100 focus-visible:ring-0"
              placeholder="flores, manga, brazo (separadas por coma)"
              data-testid="tags-input"
            />
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={uploading || !imageUrl}
          className="w-full bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold py-6 h-auto"
          data-testid="publish-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Publicando...
            </>
          ) : (
            <>
              <UploadIcon className="w-4 h-4 mr-2" />
              Publicar Trabajo
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default Upload;
